import {
    Area,
    AreaChart,
    CartesianGrid,
    defs,
    Legend,
    linearGradient,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {Box, Card, CardContent, Typography} from "@mui/material";
import {ChartTooltip, COLORS} from "../../../ui/Theme";
import {ListDivider} from "../../../ui/ListStyles";
import NotFound from "../../../components/notfound/NotFound";

const CustomTooltip = ({active, payload, chartType}) => {
    if (active && payload && payload.length > 0 && payload[0].payload) {
        const {x, y, percentY} = payload[0].payload;
        return (
            <ChartTooltip>
                <Box sx={{display: "flex", alignItems: "center", gap: "5px"}}>
                    <Typography sx={{fontSize: "12px"}}>
                        قیمت:
                    </Typography>
                    <Typography sx={{fontSize: "12px", fontWeight: 900}} dir="ltr">
                        {parseFloat(x.toFixed(2)).toLocaleString("fa-IR")}
                    </Typography>
                </Box>
                <ListDivider flexItem/>
                {chartType === "percent" ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: COLORS.ORANGE,
                        }}
                    >
                        <Typography sx={{fontSize: "12px"}}>سود به درصد</Typography>
                        <Typography sx={{fontSize: "12px", fontWeight: 900}} dir="ltr">
                            {parseFloat(percentY.toFixed(2)).toLocaleString("fa-IR")}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: COLORS.BLUE,
                        }}
                    >
                        <Typography sx={{fontSize: "12px"}}>سود به قیمت</Typography>
                        <Typography sx={{fontSize: "12px", fontWeight: 900}} dir="ltr">
                            {parseFloat(y.toFixed(2)).toLocaleString("fa-IR")}
                        </Typography>
                    </Box>
                )}
            </ChartTooltip>
        );
    }
    return null;
};

const formatTick = (value) => {
    if (value >= 1e9) {
        return (value / 1e9).toFixed(1) + 'B';
    } else if (value >= 1e6) {
        return (value / 1e6).toFixed(1) + 'M';
    } else if (value >= 1e3) {
        return (value / 1e3).toFixed(0) + 'K';
    }
    return Number(value).toFixed(0);
};

const OptionCalculatorChart = ({data, chartType}) => {

    const lineKey = chartType === "percent" ? "percentY" : "y";
    const lineColor = chartType === "percent" ? COLORS.ORANGE : COLORS.BLUE;

    // set the area of color
    const gradientOffset = () => {
        const dataMax = Math.max(...data.map((i) => i.y));
        const dataMin = Math.min(...data.map((i) => i.y));

        if (dataMax <= 0) {
            return 0;
        }
        if (dataMin >= 0) {
            return 1;
        }

        return dataMax / (dataMax - dataMin);
    };

    const area = gradientOffset();

    return (
        <Card sx={{
            border: "1px solid",
            borderColor: COLORS.SOFTGRAY,
            padding: 0,
            minHeight: 500,
        }}>
         <CardContent>
             {
                 data.length ? (
                     <ResponsiveContainer width="100%" height={500} style={{padding: "10px"}}>
                         <AreaChart
                             syncId="calculator"
                             data={data}
                             margin={{ top: 0, right: 0, bottom: 20, left: 0 }}
                         >
                             <defs>
                                 <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                                     <stop offset={area} stopColor={COLORS.GREEN} stopOpacity={0.5}/>
                                     <stop offset={area} stopColor={COLORS.RED} stopOpacity={0.5}/>
                                 </linearGradient>
                             </defs>
                             <CartesianGrid strokeDasharray="0 0" stroke={COLORS.SOFTGRAY} />
                             <Tooltip content={<CustomTooltip chartType={chartType}/>} cursor={{ fill: COLORS.SOFTGRAY }}/>
                             <XAxis
                                 stroke={COLORS.GRAY}
                                 dataKey="x"
                                 tickMargin={10}
                                 tickFormatter={formatTick}
                                 textAnchor="middle"
                             />
                             <YAxis
                                 tickFormatter={formatTick}
                                 textAnchor="start"
                                 tickMargin={10}
                                 stroke={COLORS.GRAY}
                                 domain={["auto", "auto"]}
                             />
                             <Legend
                                 wrapperStyle={{
                                     width: "fit-content",
                                     top: -10,
                                     right: 0,
                                     lineHeight: "20px",
                                 }}
                                 verticalAlign="top"
                                 formatter={(value) => {
                                     if (value === "y") {
                                         return <span style={{ marginRight: "5px" }}>سود به قیمت</span>;
                                     } else if (value === "percentY")
                                         return <span style={{ marginRight: "5px" }}>سود به درصد</span>;
                                     return value;
                                 }}
                             />
                             <Area
                                 type="monotone"
                                 dataKey={lineKey}
                                 stroke={lineColor}
                                 fill="url(#valueGradient)"
                             />
                         </AreaChart>
                     </ResponsiveContainer>
                 ) : (
                     <Box sx={{ height: 500 }}>
                         <NotFound/>
                     </Box>
                 )
             }
         </CardContent>
        </Card>
    );
};

export default OptionCalculatorChart;